import type { PublicClient } from 'viem';
import { parseEther } from 'viem';
import { AAConfigABI, CONTRACT_ADDRESSES, CREATOR_DEPOSIT } from '../config/contracts';
import { DEFAULT_RELAUNCH_MULTIPLIER } from './relaunchCost';
import { getBasePublicClient } from './basePublicClient';
import { normalizeHexAddress } from './protocolDeployments';

export type AAConfigRelaunchCost = {
  launchCost: bigint;
  relaunchMultiplier: bigint;
};

/** Matches current Base AAConfig deployment — unblocks UI if RPC fetch hangs. */
export const AA_CONFIG_RELAUNCH_DEFAULTS: AAConfigRelaunchCost = {
  launchCost: parseEther(String(CREATOR_DEPOSIT)),
  relaunchMultiplier: DEFAULT_RELAUNCH_MULTIPLIER,
};

const cacheByAddr = new Map<string, AAConfigRelaunchCost>();
const inflightByAddr = new Map<string, Promise<AAConfigRelaunchCost>>();

function configKey(configAddr?: string | null): string {
  return (normalizeHexAddress(configAddr) ?? (CONTRACT_ADDRESSES.AAConfig as `0x${string}`)).toLowerCase();
}

export function getCachedAAConfigRelaunchCost(configAddr?: string | null): AAConfigRelaunchCost | null {
  return cacheByAddr.get(configKey(configAddr)) ?? null;
}

/** AAConfig relaunch fee params — use public client so Market Detail is not blocked by wagmi read queue. */
export async function fetchAAConfigRelaunchCost(
  client?: PublicClient,
  configAddr?: string | null,
): Promise<AAConfigRelaunchCost> {
  const address = (normalizeHexAddress(configAddr) ??
    (CONTRACT_ADDRESSES.AAConfig as `0x${string}`)) as `0x${string}`;
  const key = address.toLowerCase();
  const cached = cacheByAddr.get(key);
  if (cached) return cached;
  const existing = inflightByAddr.get(key);
  if (existing) return existing;

  const inflight = (async () => {
    const c = client ?? getBasePublicClient();
    const [launchCost, relaunchMultiplier] = await Promise.all([
      c.readContract({ address, abi: AAConfigABI, functionName: 'launchCost' }),
      c.readContract({ address, abi: AAConfigABI, functionName: 'relaunchMultiplier' }),
    ]);
    const result: AAConfigRelaunchCost = {
      launchCost: BigInt(launchCost as bigint | number | string),
      relaunchMultiplier: BigInt(relaunchMultiplier as bigint | number | string),
    };
    cacheByAddr.set(key, result);
    return result;
  })();

  inflightByAddr.set(key, inflight);
  try {
    return await inflight;
  } finally {
    inflightByAddr.delete(key);
  }
}

/** Warm cache on app boot — Market Detail should not wait on first paint. */
void fetchAAConfigRelaunchCost().catch(() => {
  /* keep defaults */
});
